import { getCachedStorefrontConfigSync, getStorefrontConfig } from './storefrontConfig';

/**
 * Bundle offers — "buy these together for RM50".
 *
 * Configured by the merchant (admin → Bundle Offers), stored on the tenant
 * config, and published in the storefront config.
 *
 * IMPORTANT: everything here is a PREVIEW. The price a shopper actually pays
 * is recomputed by `applyBundleOffers()` in the backend's checkout.service
 * from the same config. Keep the matching rules in this file in step with
 * that one — if they drift, the cart quotes a total the server won't honour.
 */

export interface BundleOffer {
  id: string;
  name: string;
  description?: string;
  productIds: number[];
  bundlePriceInt: number;
  active?: boolean;
}

/** A cart line, reduced to what bundle matching needs. */
export interface BundleLine {
  productId: number;
  quantity: number;
  /** Effective unit price in cents, after options/line discounts. */
  unitPriceInt: number;
}

export interface AppliedBundle {
  id: string;
  name: string;
  /** How many complete sets matched. */
  sets: number;
  savingsInt: number;
}

export interface BundleResult {
  applied: AppliedBundle[];
  /** Total to subtract from the cart subtotal, in cents. */
  savingsInt: number;
}

const EMPTY: BundleResult = { applied: [], savingsInt: 0 };

const parseOffer = (raw: any): BundleOffer | null => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.active === false) return null;

  const productIds = Array.isArray(raw.productIds)
    ? Array.from(
        new Set(
          raw.productIds
            .map((id: any) => Number(id))
            .filter((id: number) => Number.isInteger(id) && id > 0),
        ),
      )
    : [];
  const bundlePriceInt = Number(raw.bundlePriceInt);
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';

  if (!name || productIds.length < 2) return null;
  if (!Number.isFinite(bundlePriceInt) || bundlePriceInt < 0) return null;

  return {
    id: String(raw.id || ''),
    name,
    description: typeof raw.description === 'string' ? raw.description : undefined,
    productIds: productIds as number[],
    bundlePriceInt: Math.round(bundlePriceInt),
    active: true,
  };
};

/** Active offers from the already-loaded storefront config (no fetch). */
export function getBundleOffersSync(): BundleOffer[] {
  const raw = getCachedStorefrontConfigSync()?.bundleOffers;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(parseOffer)
    .filter((offer): offer is BundleOffer => offer !== null);
}

/** Active offers, loading the storefront config first if it isn't cached yet. */
export async function loadBundleOffers(): Promise<BundleOffer[]> {
  const sync = getBundleOffersSync();
  if (sync.length > 0) return sync;
  try {
    const config = await getStorefrontConfig();
    const raw = config?.bundleOffers;
    if (!Array.isArray(raw)) return [];
    return raw
      .map(parseOffer)
      .filter((offer): offer is BundleOffer => offer !== null);
  } catch {
    return [];
  }
}

/**
 * Match offers against cart lines and total the savings.
 *
 * Mirrors the server: a set only counts when EVERY member is present, sets
 * repeat while stock in the cart allows, each line's units are consumed once
 * across all offers, and a "bundle" that costs more than buying separately is
 * ignored rather than applied.
 */
export function applyBundleOffers(
  lines: BundleLine[],
  offers: BundleOffer[] = getBundleOffersSync(),
): BundleResult {
  if (!offers.length || !lines.length) return EMPTY;

  const available = lines.map((line) => Math.max(0, line.quantity));
  const applied: AppliedBundle[] = [];
  let savingsInt = 0;

  for (const offer of offers) {
    for (;;) {
      const picks: number[] = [];
      for (const productId of offer.productIds) {
        const idx = lines.findIndex(
          (line, i) =>
            Number(line.productId) === productId &&
            available[i] > 0 &&
            !picks.includes(i),
        );
        if (idx === -1) break;
        picks.push(idx);
      }
      if (picks.length !== offer.productIds.length) break;

      const sets = Math.min(...picks.map((i) => available[i]));
      const baseTotalPerSet = picks.reduce(
        (sum, i) => sum + lines[i].unitPriceInt,
        0,
      );
      if (offer.bundlePriceInt >= baseTotalPerSet) break;

      const setSavings = (baseTotalPerSet - offer.bundlePriceInt) * sets;
      savingsInt += setSavings;
      applied.push({
        id: offer.id,
        name: offer.name,
        sets,
        savingsInt: setSavings,
      });
      picks.forEach((i) => {
        available[i] -= sets;
      });
    }
  }

  return applied.length > 0 ? { applied, savingsInt } : EMPTY;
}
